import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Target, TrendingUp, Users, Save, RefreshCw, Calendar } from "lucide-react";

const MONTHS = [
  "Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
  "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre",
];
const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProduzioneObiettivoPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [availableTechs, setAvailableTechs] = useState([]);
  const [bep, setBep] = useState(0);
  const [fattTarget, setFattTarget] = useState(0);
  const [dailyDefault, setDailyDefault] = useState("");
  const [tecnici, setTecnici] = useState([]); // [{ user_id, override_daily }]
  const [collab, setCollab] = useState([]);   // [{ name, lordo, margine_percent }]
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [r, tr] = await Promise.all([
        api.get("/production-targets", { params: { year, month } }),
        api.get("/users", { params: { role: "tecnico,subappalto" } }),
      ]);
      setData(r.data);
      setBep(r.data.obiettivo_produzione || 0);
      setFattTarget(r.data.fatturato_lordo_obiettivo || 0);
      setDailyDefault(r.data.obiettivo_giornaliero_default || "");
      setTecnici((r.data.tecnici || []).map((t) => ({ user_id: t.user_id, override_daily: t.override_daily ?? "" })));
      setCollab((r.data.collaboratori || []).map((c) => ({
        user_id: c.user_id || null,
        name: c.name || "",
        lordo: c.lordo || 0,  // usato solo se legacy (senza user_id)
        margine_percent: c.margine_percent ?? 20,
      })));
      setAvailableTechs(tr.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento"); }
    finally { setBusy(false); }
  };

  // Refresh solo del progressivo dal backend (senza toccare state locale non salvato)
  const refreshProgressivo = async () => {
    try {
      const r = await api.get("/production-targets", { params: { year, month } });
      setData(r.data);
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  // Auto-refresh: quando la finestra torna in focus + polling ogni 60 secondi
  useEffect(() => {
    const onFocus = () => refreshProgressivo();
    window.addEventListener("focus", onFocus);
    const intv = setInterval(refreshProgressivo, 60000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(intv);
    };
    // eslint-disable-next-line
  }, [year, month]);

  const usedIds = new Set(tecnici.map((t) => t.user_id));
  const usedCollabIds = new Set(collab.map((c) => c.user_id).filter(Boolean));
  const addableTechs = availableTechs.filter((u) => !usedIds.has(u.id));
  // Collaboratori esterni: prendono dalla stessa lista tecnici (dipendenti + p.iva),
  // escludendo quelli già presenti in Personale Sociale e i già selezionati come collaboratori.
  const addableCollab = availableTechs.filter((u) => !usedIds.has(u.id) && !usedCollabIds.has(u.id));

  const addTech = (uid) => {
    if (!uid || usedIds.has(uid)) return;
    setTecnici((prev) => [...prev, { user_id: uid, override_daily: "" }]);
  };
  const removeTech = (uid) => setTecnici((prev) => prev.filter((t) => t.user_id !== uid));

  const addCollab = (uid) => {
    if (!uid || usedCollabIds.has(uid)) return;
    const u = availableTechs.find((x) => x.id === uid);
    if (!u) return;
    setCollab((prev) => [...prev, { user_id: uid, name: u.name, lordo: 0, margine_percent: 20 }]);
  };
  const removeCollab = (idx) => setCollab((prev) => prev.filter((_, i) => i !== idx));
  const patchCollab = (idx, patch) => setCollab((prev) => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        year, month,
        obiettivo_produzione: Number(bep) || 0,
        fatturato_lordo_obiettivo: Number(fattTarget) || 0,
        obiettivo_giornaliero_default: dailyDefault === "" ? null : Number(dailyDefault),
        tecnici: tecnici.map((t) => ({
          user_id: t.user_id,
          override_daily: t.override_daily === "" || t.override_daily === null ? null : Number(t.override_daily),
        })),
        collaboratori: collab.map((c) => ({
          user_id: c.user_id || null,
          name: (c.name || "").trim(),
          lordo: Number(c.lordo) || 0,
          margine_percent: Number(c.margine_percent) || 0,
        })).filter((c) => c.name),
      };
      await api.put("/production-targets", payload);
      toast.success("Obiettivi salvati");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  // Mappa progressivo dal backend (indexed by user_id) - include TUTTI i tecnici del tenant
  const progressivoMap = useMemo(() => {
    if (data?.progressivo_per_tecnico && typeof data.progressivo_per_tecnico === "object") {
      return data.progressivo_per_tecnico;
    }
    const m = {};
    for (const t of (data?.tecnici || [])) m[t.user_id] = t.progressivo || 0;
    return m;
  }, [data]);

  // Per ogni collaboratore risolve il lordo effettivo:
  //   - se user_id → dalle lavorazioni chiuse (auto, reattivo)
  //   - altrimenti → dal valore inserito manualmente
  const effectiveLordo = (c) => c.user_id ? (progressivoMap[c.user_id] || 0) : (Number(c.lordo) || 0);

  // Filtri date per sezione (default: tutto il mese)
  const pad2 = (n) => String(n).padStart(2, "0");
  const monthFrom = `${year}-${pad2(month)}-01`;
  const monthTo = `${year}-${pad2(month)}-${pad2(new Date(year, month, 0).getDate())}`;
  const [socRange, setSocRange] = useState({ from: "", to: "" });
  const [colRange, setColRange] = useState({ from: "", to: "" });
  useEffect(() => { setSocRange({ from: "", to: "" }); setColRange({ from: "", to: "" }); }, [year, month]);
  const isFiltered = (r) => !!(r.from || r.to);
  const rangeOf = (r) => ({ from: r.from || monthFrom, to: r.to || monthTo });
  const sumRange = (uid, r) => {
    const { from, to } = rangeOf(r);
    let s = 0;
    for (const [d, v] of Object.entries(data?.daily_progress || {})) {
      if (d >= from && d <= to) s += Number(v?.per_tecnico?.[uid]) || 0;
    }
    return s;
  };
  const workDaysIn = (r) => {
    const { from, to } = rangeOf(r);
    let n = 0;
    for (let d = new Date(`${from}T12:00:00`); d <= new Date(`${to}T12:00:00`); d.setDate(d.getDate() + 1)) if (d.getDay() !== 0) n += 1;
    return n;
  };
  const colLordo = (c) => (isFiltered(colRange) ? (c.user_id ? sumRange(c.user_id, colRange) : null) : effectiveLordo(c));

  const totalMargineEsterni = useMemo(() => collab.reduce((s, c) => s + effectiveLordo(c) * (Number(c.margine_percent) || 0) / 100, 0), [collab, progressivoMap]);
  const totalLordoEsterni = useMemo(() => collab.reduce((s, c) => s + effectiveLordo(c), 0), [collab, progressivoMap]);

  // Calcoli REATTIVI dal state locale (indipendenti dal salvataggio)
  const workingDays = data?.working_days || 22;
  const bepNum = Number(bep) || 0;
  const fattTargetNum = Number(fattTarget) || 0;
  const nTec = tecnici.length;
  const dailyDefaultCalc = dailyDefault !== "" && dailyDefault !== null
    ? Number(dailyDefault) || 0
    : (workingDays > 0 ? bepNum / workingDays : 0);
  const perTecMonthly = nTec > 0 ? bepNum / nTec : 0;
  const perTecDaily = workingDays > 0 ? perTecMonthly / workingDays : 0;

  // Righe tecnici calcolate reattivamente
  const rowsTecnici = useMemo(() => tecnici.map((lt) => {
    const uid = lt.user_id;
    const name = availableTechs.find((u) => u.id === uid)?.name
                 || (data?.tecnici || []).find((x) => x.user_id === uid)?.name
                 || "—";
    const targetDaily = lt.override_daily !== "" && lt.override_daily !== null && lt.override_daily !== undefined
      ? Number(lt.override_daily) || 0
      : perTecDaily;
    const targetMonthly = targetDaily * workingDays;
    const prog = progressivoMap[uid] || 0;
    const pct = targetMonthly > 0 ? (prog / targetMonthly * 100) : 0;
    return { user_id: uid, name, target_daily: targetDaily, target_monthly: targetMonthly, progressivo: prog, pct_raggiunto: pct };
  }), [tecnici, availableTechs, data, perTecDaily, workingDays, progressivoMap]);

  // Griglia giornaliera del mese: [{date, weekday, total, isSunday, isFuture, isToday}]
  // Il totale conta SOLO la produzione dei tecnici in Personale Sociale (non collaboratori esterni).
  const dailyGrid = useMemo(() => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const today = new Date();
    // Data odierna in LOCAL (evita bug timezone con toISOString che converte in UTC)
    const pad = (n) => String(n).padStart(2, "0");
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const isCurrentMonth = today.getFullYear() === year && (today.getMonth() + 1) === month;
    const dp = data?.daily_progress || {};
    const configuredIds = new Set(tecnici.map((t) => t.user_id).filter(Boolean));
    const out = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(year, month - 1, d);
      // Chiave locale (senza timezone shift): il DB salva "YYYY-MM-DD" senza conversione UTC
      const iso = `${year}-${pad(month)}-${pad(d)}`;
      const wd = dateObj.getDay(); // 0=dom
      let total = 0;
      const perTec = dp[iso]?.per_tecnico || {};
      for (const [uid, val] of Object.entries(perTec)) {
        if (configuredIds.has(uid)) total += Number(val) || 0;
      }
      const isFuture = isCurrentMonth && iso > todayStr;
      const isToday = iso === todayStr;
      const wdShort = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"][wd];
      out.push({ date: iso, day: d, weekday: wdShort, isSunday: wd === 0, total, isFuture, isToday });
    }
    return out;
  }, [data, year, month, tecnici]);

  const progressivoSociali = useMemo(() => rowsTecnici.reduce((s, r) => s + r.progressivo, 0), [rowsTecnici]);
  // Il "Progressivo verso BEP" include anche il margine Evolve dei collaboratori esterni.
  const progressivoBep = progressivoSociali + totalMargineEsterni;
  const pctBep = bepNum > 0 ? (progressivoBep / bepNum * 100) : 0;
  const utile = progressivoBep - bepNum;
  // Fatturato lordo totale = produzione tecnici sociali + LORDO collaboratori esterni
  const fattLordoTotale = progressivoSociali + totalLordoEsterni;
  const pctFattLordo = fattTargetNum > 0 ? (fattLordoTotale / fattTargetNum * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Obiettivi mensili</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">Produzione Obiettivo</h1>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs text-slate-500">Mese</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="obj-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Anno</Label>
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} className="w-24 rounded-sm" data-testid="obj-year" />
          </div>
          <Button variant="outline" className="rounded-sm" onClick={load} disabled={busy} data-testid="obj-reload">
            <RefreshCw className="mr-2 h-4 w-4" /> Ricarica
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="obj-save">
            <Save className="mr-2 h-4 w-4" /> {saving ? "Salvo…" : "Salva"}
          </Button>
        </div>
      </div>

      {data?.copied_from ? (
        <div
          className="rounded-sm border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900"
          data-testid="obj-copied-banner"
        >
          <b>Dati riportati automaticamente</b> dal mese di {MONTHS[(data.copied_from.month || 1) - 1]} {data.copied_from.year}.
          Puoi aggiungere/rimuovere tecnici e modificare gli obiettivi, poi cliccare <b>Salva</b>.
        </div>
      ) : null}

      {/* KPI Card */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="rounded-sm border border-slate-200 bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Progressivo verso BEP</div>
          <div className="mt-1 text-2xl font-bold text-slate-900" data-testid="kpi-prog-sociali">{eur(progressivoBep)}</div>
          <div className="mt-1 text-xs text-slate-500">
            <span className={`font-mono font-semibold ${pctBep >= 100 ? "text-emerald-600" : "text-blue-600"}`}>{pctBep.toFixed(1)}%</span> del BEP
            <span className="ml-1">· Sociali <b>{eur(progressivoSociali)}</b> + Marg. collab <b>{eur(totalMargineEsterni)}</b></span>
          </div>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">BEP mese</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{eur(bepNum)}</div>
          <div className={`mt-1 text-xs ${utile >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            Utile: <span className="font-mono font-semibold">{eur(utile)}</span>
          </div>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Fatt. Lordo Totale</div>
          <div className="mt-1 text-2xl font-bold text-slate-900" data-testid="kpi-fatt-tot">{eur(fattLordoTotale)}</div>
          <div className="mt-1 text-xs text-slate-500">
            <span className={`font-mono font-semibold ${pctFattLordo >= 100 ? "text-emerald-600" : "text-blue-600"}`}>{pctFattLordo.toFixed(1)}%</span> dell'obiettivo
            <span className="ml-1">· Sociali <b>{eur(progressivoSociali)}</b> + Lordo collab <b>{eur(totalLordoEsterni)}</b></span>
          </div>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Giornaliero Team</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{eur(dailyDefaultCalc)}</div>
          <div className="mt-1 text-xs text-slate-500">
            {workingDays} giorni lav. · {nTec} tec. → {eur(perTecDaily)}/tec/giorno
          </div>
        </div>
      </div>

      {/* Produzione giornaliera */}
      <div className="rounded-sm border border-slate-200 bg-white p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-indigo-600" />
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Produzione giornaliera</div>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-emerald-200"></span> ≥ obiettivo</span>
            <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-red-200"></span> sotto obiettivo / non prodotto</span>
            <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-slate-100"></span> futuro / domenica</span>
            <span className="ml-2">Target: <b className="font-mono">{eur(dailyDefaultCalc)}</b></span>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-14 xl:grid-cols-16">
          {dailyGrid.map((d) => {
            const target = dailyDefaultCalc;
            const hit = target > 0 && d.total >= target;
            const under = target > 0 && d.total < target;  // include anche 0 (non prodotto)
            // I giorni futuri restano neutri SOLO se non hanno ancora produzione.
            // Se c'è già produzione (lavorazioni caricate in anticipo), applica hit/under normali.
            const isNeutralFuture = d.isFuture && d.total === 0;
            const bg = isNeutralFuture || d.isSunday
              ? "border-slate-200 bg-slate-50 text-slate-400"
              : hit
              ? "border-emerald-400 bg-emerald-100 text-emerald-900"
              : under
              ? "border-red-300 bg-red-100 text-red-900"
              : "border-slate-200 bg-slate-50 text-slate-500";
            const pct = target > 0 ? Math.round((d.total / target) * 100) : 0;
            return (
              <div
                key={d.date}
                className={`rounded-sm border p-2 text-center ${bg} ${d.isSunday ? "opacity-60" : ""} ${d.isToday ? "ring-2 ring-blue-500" : ""}`}
                data-testid={`day-${d.date}`}
                title={`${d.weekday} ${d.day}: prodotto ${eur(d.total)} · target ${eur(target)} (${pct}%)`}
              >
                <div className="text-[10px] font-bold uppercase tracking-wider">{d.weekday}</div>
                <div className="text-lg font-bold leading-tight">{d.day}</div>
                <div className="mt-0.5 font-mono text-[11px] font-semibold">
                  {d.total > 0 ? `€${d.total.toLocaleString("it-IT", { maximumFractionDigits: 0 })}` : "—"}
                </div>
                {d.total > 0 && target > 0 && (
                  <div className={`text-[10px] font-mono font-bold ${hit ? "text-emerald-700" : "text-red-700"}`}>{pct}%</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Impostazioni obiettivi */}
      <div className="rounded-sm border border-slate-200 bg-white p-5">
        <div className="mb-4 flex items-center gap-2">
          <Target className="h-4 w-4 text-blue-600" />
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Impostazioni obiettivi mensili</div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <Label>Produzione Obiettivo / BEP (€)</Label>
            <Input type="number" step="0.01" value={bep} onChange={(e) => setBep(e.target.value)} className="rounded-sm font-mono" data-testid="obj-bep" />
          </div>
          <div>
            <Label>Fatturato Lordo Obiettivo (€)</Label>
            <Input type="number" step="0.01" value={fattTarget} onChange={(e) => setFattTarget(e.target.value)} className="rounded-sm font-mono" data-testid="obj-fatt" />
          </div>
          <div>
            <Label>Obiettivo Giornaliero (€, opz.)</Label>
            <Input type="number" step="0.01" value={dailyDefault} onChange={(e) => setDailyDefault(e.target.value)} placeholder={`auto: ${(bepNum / Math.max(1, workingDays)).toFixed(2)}`} className="rounded-sm font-mono" data-testid="obj-daily" />
          </div>
        </div>
      </div>

      {/* Personale Sociale */}
      <div className="rounded-sm border border-slate-200 bg-white p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-emerald-600" />
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Personale Sociale</div>
            <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-xs">{tecnici.length}</span>
          </div>
          {addableTechs.length > 0 && (
            <Select value="" onValueChange={addTech}>
              <SelectTrigger className="w-56 rounded-sm" data-testid="add-tech-select"><SelectValue placeholder="+ Aggiungi tecnico…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {addableTechs.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <DateRangeFilter range={socRange} setRange={setSocRange} min={monthFrom} max={monthTo} testid="soc-range" />

        {tecnici.length === 0 ? (
          <div className="rounded-sm border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">Nessun tecnico. Aggiungine uno per iniziare a tracciare gli obiettivi.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="p-2">Tecnico</th>
                  <th className="p-2 text-right">{isFiltered(socRange) ? "Obiettivo periodo" : "Obiettivo Mese"}</th>
                  <th className="p-2 text-right">Obiettivo/Giorno</th>
                  <th className="p-2 text-right">{isFiltered(socRange) ? "Prodotto nel periodo" : "Prodotto"}</th>
                  <th className="p-2 text-right">% Raggiunto</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {rowsTecnici.map((t0) => {
                  const f = isFiltered(socRange);
                  const t = f ? (() => {
                    const tm = t0.target_daily * workDaysIn(socRange);
                    const pr = sumRange(t0.user_id, socRange);
                    return { ...t0, target_monthly: tm, progressivo: pr, pct_raggiunto: tm > 0 ? pr / tm * 100 : 0 };
                  })() : t0;
                  const local = tecnici.find((x) => x.user_id === t.user_id);
                  const pctColor = t.pct_raggiunto >= 100 ? "text-emerald-600" : t.pct_raggiunto >= 60 ? "text-amber-600" : "text-red-600";
                  return (
                    <tr key={t.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-tec-${t.user_id}`}>
                      <td className="p-2 font-semibold text-slate-900">{t.name}</td>
                      <td className="p-2 text-right font-mono">{eur(t.target_monthly)}</td>
                      <td className="p-2 text-right">
                        <Input
                          type="number" step="0.01"
                          value={local?.override_daily ?? ""}
                          placeholder={t.target_daily?.toFixed(2)}
                          onChange={(e) => setTecnici((prev) => prev.map((x) => x.user_id === t.user_id ? { ...x, override_daily: e.target.value } : x))}
                          className="h-8 w-28 rounded-sm text-right font-mono"
                          data-testid={`daily-${t.user_id}`}
                        />
                      </td>
                      <td className="p-2 text-right font-mono font-semibold">{eur(t.progressivo)}</td>
                      <td className={`p-2 text-right font-mono font-bold ${pctColor}`}>{t.pct_raggiunto.toFixed(1)}%</td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => removeTech(t.user_id)} data-testid={`rm-tec-${t.user_id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <SocTotals rows={rowsTecnici} filtered={isFiltered(socRange)} sumRange={(u) => sumRange(u, socRange)} days={workDaysIn(socRange)} bepNum={bepNum} progressivoSociali={progressivoSociali} />
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Collaboratori esterni */}
      <div className="rounded-sm border border-slate-200 bg-white p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-amber-600" />
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Collaboratori esterni</div>
            <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-xs">{collab.length}</span>
          </div>
          {addableCollab.length > 0 && (
            <Select value="" onValueChange={addCollab}>
              <SelectTrigger className="w-64 rounded-sm" data-testid="add-collab-select"><SelectValue placeholder="+ Aggiungi collaboratore…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {addableCollab.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <DateRangeFilter range={colRange} setRange={setColRange} min={monthFrom} max={monthTo} testid="col-range" />

        {collab.length === 0 ? (
          <div className="rounded-sm border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">Nessun collaboratore esterno.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="p-2">Nome</th>
                  <th className="p-2 text-right">{isFiltered(colRange) ? "Lordo nel periodo (€)" : "Lordo (€)"}</th>
                  <th className="p-2 text-right">% Margine Evolve</th>
                  <th className="p-2 text-right">{isFiltered(colRange) ? "Margine nel periodo (€)" : "Margine Evolve (€)"}</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {collab.map((c, idx) => {
                  const lordoEff = colLordo(c);
                  const margine = lordoEff == null ? null : lordoEff * (Number(c.margine_percent) || 0) / 100;
                  const isAuto = !!c.user_id;
                  const colF = isFiltered(colRange);
                  return (
                    <tr key={idx} className="border-b border-slate-100" data-testid={`row-collab-${idx}`}>
                      <td className="p-2 font-semibold text-slate-900">
                        {c.name}
                        {isAuto && <span className="ml-2 rounded-sm border border-emerald-300 bg-emerald-50 px-1 py-0.5 text-[10px] font-mono text-emerald-700">AUTO</span>}
                      </td>
                      <td className="p-2 text-right">
                        {isAuto || colF ? (
                          <span className="font-mono font-semibold text-slate-800" data-testid={`collab-lordo-${idx}`}>{lordoEff == null ? <span className="text-xs font-normal text-slate-400" title="Valore mensile inserito a mano: non disponibile per giorno">n/d (manuale)</span> : eur(lordoEff)}</span>
                        ) : (
                          <Input type="number" step="0.01" value={c.lordo} onChange={(e) => patchCollab(idx, { lordo: e.target.value })} className="h-8 rounded-sm text-right font-mono" data-testid={`collab-lordo-${idx}`} />
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <Input type="number" step="0.1" value={c.margine_percent} onChange={(e) => patchCollab(idx, { margine_percent: e.target.value })} className="h-8 w-24 rounded-sm text-right font-mono" data-testid={`collab-mgn-${idx}`} />
                      </td>
                      <td className="p-2 text-right font-mono font-semibold text-emerald-700" data-testid={`collab-margine-${idx}`}>{margine == null ? "—" : eur(margine)}</td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => removeCollab(idx)} data-testid={`rm-collab-${idx}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                  <td className="p-2">TOTALE</td>
                  <td className="p-2 text-right font-mono" data-testid="collab-total-lordo">{eur(isFiltered(colRange) ? collab.reduce((s, c) => s + (colLordo(c) || 0), 0) : totalLordoEsterni)}</td>
                  <td className="p-2"></td>
                  <td className="p-2 text-right font-mono text-emerald-700" data-testid="collab-total-margine">{eur(isFiltered(colRange) ? collab.reduce((s, c) => s + (colLordo(c) || 0) * (Number(c.margine_percent) || 0) / 100, 0) : totalMargineEsterni)}</td>
                  <td className="p-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DateRangeFilter({ range, setRange, min, max, testid }) {
  const active = !!(range.from || range.to);
  const oneDay = (v) => setRange({ from: v, to: v });
  return (
    <div className="mb-3 flex flex-wrap items-end gap-2 rounded-sm bg-slate-50 p-2" data-testid={testid}>
      <Calendar className="mb-2 h-4 w-4 text-slate-500" />
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Dal</Label>
        <Input type="date" min={min} max={max} value={range.from} onChange={(e) => setRange((r) => ({ from: e.target.value, to: r.to && r.to < e.target.value ? e.target.value : r.to }))} className="h-8 w-40 rounded-sm" data-testid={`${testid}-from`} />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Al</Label>
        <Input type="date" min={range.from || min} max={max} value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="h-8 w-40 rounded-sm" data-testid={`${testid}-to`} />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Solo il giorno</Label>
        <Input type="date" min={min} max={max} value={range.from && range.from === range.to ? range.from : ""} onChange={(e) => oneDay(e.target.value)} className="h-8 w-40 rounded-sm" data-testid={`${testid}-day`} />
      </div>
      <Button size="sm" variant={active ? "outline" : "ghost"} disabled={!active} onClick={() => setRange({ from: "", to: "" })} className="h-8 rounded-sm" data-testid={`${testid}-reset`}>Tutto il mese</Button>
      {active && <span className="mb-1.5 text-xs font-semibold text-[#0052FF]">Filtro attivo</span>}
    </div>
  );
}

function SocTotals({ rows, filtered, sumRange, days, bepNum, progressivoSociali }) {
  const prod = filtered ? rows.reduce((s, r) => s + sumRange(r.user_id), 0) : progressivoSociali;
  const target = filtered ? rows.reduce((s, r) => s + r.target_daily * days, 0) : bepNum;
  const pct = target > 0 ? prod / target * 100 : 0;
  return (
    <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
      <td className="p-2">TOTALE</td>
      <td className="p-2 text-right font-mono">{eur(target)}</td>
      <td className="p-2"></td>
      <td className="p-2 text-right font-mono" title="Somma della produzione del Personale Sociale (esclude margine collab. esterni)" data-testid="soc-total-prod">{eur(prod)}</td>
      <td className={`p-2 text-right font-mono ${pct >= 100 ? "text-emerald-600" : "text-blue-600"}`}>{pct.toFixed(1)}%</td>
      <td className="p-2"></td>
    </tr>
  );
}

